/**
 * Block 13 PR-G Phase 2 Step 2 — Uzbek (uz, Latin script) amount-in-words.
 *
 * Output convention: words only (no currency word appended). Uzbek has no
 * grammatical gender and no plural form changes for cardinal numbers, so the
 * algorithm is simpler than Russian. Examples:
 *   1 → "bir"
 *   1000 → "bir ming" (explicit "bir", consistent with millions/billions)
 *   1_234_567 → "bir million ikki yuz o'ttiz to'rt ming besh yuz oltmish yetti"
 *
 * "so'm" indeclinable — the same form regardless of count. Currency word is
 * appended by Step 3 `contractTextRender.ts`, not here.
 *
 * **Apostrophe convention.** Per Phase 2 Step 1 verification (ADR-0025 §Q4
 * amendment), production templates use ASCII apostrophe (U+0027), not the
 * typographic U+02BB MODIFIER LETTER TURNED COMMA. PT Sans doesn't cover U+02BB
 * anyway. Output here uses ASCII apostrophes (e.g., "o'n", "to'rt", "so'm")
 * for consistency with templates and clean rendering through `renderContractToPdf`
 * (`normalizeApostrophes` helper в Step 4 нормализует user input the same way).
 *
 * Reference:
 *   - ADR-0025 §Q4 amendment (font verification), §Q5
 *   - Block_13_PR_G_phase_0_planning.md Phase 2 Step 2
 */

import type { AmountCurrency } from './ru';

const UNITS = [
  '',
  'bir',
  'ikki',
  'uch',
  "to'rt",
  'besh',
  'olti',
  'yetti',
  'sakkiz',
  "to'qqiz",
] as const;

/** Direct named tens — Uzbek tens are not formed by multiplication of "ten". */
const TENS = [
  '',
  "o'n",
  'yigirma',
  "o'ttiz",
  'qirq',
  'ellik',
  'oltmish',
  'yetmish',
  'sakson',
  "to'qson",
] as const;

/**
 * Convert 0-999 to Uzbek words. No gender / no plural — single canonical form.
 *
 * Conventions:
 *   - 100 → "yuz" (no leading "bir")
 *   - 200..900 → "{N} yuz" (e.g., "ikki yuz")
 *   - 10..19 → "o'n [N]" (e.g., "o'n bir" for 11)
 *   - 21..99 (non-tens) → "{tens} {units}" (e.g., "yigirma bir")
 */
function convertGroup(n: number): string[] {
  if (n === 0) return [];
  const result: string[] = [];
  const h = Math.floor(n / 100);
  const t = Math.floor((n % 100) / 10);
  const u = n % 10;
  // Indices гарантированно в диапазоне (h/t/u: 0-9). Non-null assertions safe
  // под `noUncheckedIndexedAccess` strict mode.
  if (h === 1) {
    result.push('yuz');
  } else if (h > 1) {
    result.push(UNITS[h]!, 'yuz');
  }
  if (t > 0) result.push(TENS[t]!);
  if (u > 0) result.push(UNITS[u]!);
  return result;
}

export function amountInWordsUz(n: number, _currency: AmountCurrency): string {
  if (!Number.isFinite(n) || !Number.isInteger(n)) {
    throw new Error(`amountInWordsUz: expected finite integer, got ${n}`);
  }
  if (n < 0) {
    return `minus ${amountInWordsUz(-n, _currency)}`;
  }
  if (n === 0) {
    return 'nol';
  }

  const billions = Math.floor(n / 1_000_000_000);
  const millions = Math.floor((n % 1_000_000_000) / 1_000_000);
  const thousands = Math.floor((n % 1_000_000) / 1000);
  const units = n % 1000;

  const parts: string[] = [];

  if (billions > 0) {
    parts.push(...convertGroup(billions), 'milliard');
  }
  if (millions > 0) {
    parts.push(...convertGroup(millions), 'million');
  }
  if (thousands > 0) {
    parts.push(...convertGroup(thousands), 'ming');
  }
  if (units > 0) {
    parts.push(...convertGroup(units));
  }

  return parts.join(' ');
}
